/*
 * <media-gallery>: product gallery (snippets/product-media-gallery.liquid).
 * Scroll-snap slider (mobile, thumbnail layouts) or grid (stacked / columns), thumbnails, arrows,
 * dots / counter, variant media switching (prepend, hide other variants, filter by option via alt text).
 * <product-video>: inline Shopify video with play/pause, sound and progress controls.
 */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.track = this.querySelector('.product-gallery__track');
        this.thumbList = this.querySelector('.product-gallery__thumbs');
        this.dots = this.querySelector('[data-gallery-dots]');
        this.counter = this.querySelector('[data-gallery-counter]');
        this.status = this.querySelector('[id^="GalleryStatus"]');
        this.mobile = window.matchMedia('(max-width: 749px)');
        if (!this.track) return;

        this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
        this.thumbList?.addEventListener('click', (e) => {
          const item = e.target.closest('[data-target]');
          if (item) this.setActiveMedia(item.dataset.target, false, true);
        });
        this.thumbPrev = this.querySelector('[data-thumbs-prev]');
        this.thumbNext = this.querySelector('[data-thumbs-next]');
        if (this.thumbList && (this.thumbPrev || this.thumbNext)) {
          this.thumbPrev?.addEventListener('click', () => this.scrollThumbs(-1));
          this.thumbNext?.addEventListener('click', () => this.scrollThumbs(1));
          this.thumbList.addEventListener('scroll', () => this.updateThumbArrows(), { passive: true });
          if ('ResizeObserver' in window) new ResizeObserver(() => this.updateThumbArrows()).observe(this.thumbList);
        }
        this.dots?.addEventListener('click', (e) => {
          const dot = e.target.closest('[data-index]');
          if (dot) this.goTo(Number(dot.dataset.index));
        });
        this.track.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.onScroll(), 80);
        }, { passive: true });
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.step(-1);
          if (e.key === 'ArrowRight') this.step(1);
        });
        this.mobile.addEventListener('change', () => this.refresh(true));

        this.syncThumbs();
        this.buildDots();
        this.refresh(true);
        this.loadAutoplayExternal();
      }

      get sectionId() {
        return this.dataset.section;
      }

      isSlider() {
        if (this.mobile.matches) return this.dataset.mobileLayout !== 'columns';
        const layout = this.dataset.desktopLayout || '';
        return layout.includes('thumbnail') || layout === 'slider';
      }

      slides() {
        return [...this.track.children].filter((s) => !s.hidden);
      }

      activeSlide() {
        return this.track.querySelector('.product-gallery__slide.is-active:not([hidden])') || this.slides()[0];
      }

      refresh(instant) {
        const slide = this.activeSlide();
        if (slide) this.setActive(slide, { scroll: true, behavior: instant ? 'instant' : 'smooth' });
      }

      step(dir) {
        const slides = this.slides();
        const index = slides.indexOf(this.activeSlide());
        this.goTo((index + dir + slides.length) % slides.length);
      }

      goTo(index, behavior = 'smooth') {
        const slide = this.slides()[index];
        if (slide) this.setActive(slide, { scroll: true, behavior });
      }

      scrollToSlide(slide, behavior = 'smooth') {
        if (!this.isSlider()) return;
        const left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
        this.track.scrollTo({ left: Math.max(0, left), behavior: behavior === 'instant' ? 'auto' : behavior });
      }

      onScroll() {
        if (!this.isSlider()) return;
        const center = this.track.scrollLeft + this.track.clientWidth / 2;
        let best = null;
        let bestDistance = Infinity;
        this.slides().forEach((slide) => {
          const d = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
          if (d < bestDistance) {
            bestDistance = d;
            best = slide;
          }
        });
        if (best && !best.classList.contains('is-active')) this.setActive(best, { scroll: false });
      }

      setActive(slide, { scroll = true, behavior = 'smooth' } = {}) {
        this.track.querySelectorAll('.product-gallery__slide').forEach((s) => s.classList.toggle('is-active', s === slide));
        if (scroll) this.scrollToSlide(slide, behavior);
        const slides = this.slides();
        const index = slides.indexOf(slide);
        if (this.thumbList) {
          this.thumbList.querySelectorAll('[data-target]').forEach((t) => {
            const current = t.dataset.target === slide.dataset.mediaId;
            const button = t.querySelector('button');
            if (current) {
              button.setAttribute('aria-current', 'true');
              const list = this.thumbList;
              if (list.scrollWidth > list.clientWidth) {
                list.scrollTo({ left: t.offsetLeft - (list.clientWidth - t.offsetWidth) / 2, behavior: 'smooth' });
              } else if (list.scrollHeight > list.clientHeight) {
                list.scrollTo({ top: t.offsetTop - (list.clientHeight - t.offsetHeight) / 2, behavior: 'smooth' });
              }
            } else {
              button.removeAttribute('aria-current');
            }
          });
        }
        this.dots?.querySelectorAll('[data-index]').forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
        const current = this.counter?.querySelector('[data-counter-current]');
        if (current) current.textContent = index + 1;
        this.pauseOthers(slide);
      }

      pauseOthers(active) {
        if (!this.isSlider()) return;
        this.track.querySelectorAll('.product-gallery__slide').forEach((s) => {
          if (s === active) return;
          s.querySelectorAll('product-video').forEach((v) => v.pause && v.pause(false));
          s.querySelectorAll('.js-youtube').forEach((f) => f.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'));
          s.querySelectorAll('.js-vimeo').forEach((f) => f.contentWindow?.postMessage('{"method":"pause"}', '*'));
        });
        active.querySelectorAll('deferred-media[data-autoload]').forEach((d) => d.loadContent && d.loadContent(false));
      }

      loadAutoplayExternal() {
        if (this.isSlider()) {
          this.activeSlide()?.querySelectorAll('deferred-media[data-autoload]').forEach((d) => d.loadContent && d.loadContent(false));
          return;
        }
        this.querySelectorAll('deferred-media[data-autoload]').forEach((d) => {
          new IntersectionObserver((entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.disconnect();
            d.loadContent && d.loadContent(false);
          }).observe(d);
        });
      }

      syncThumbs() {
        if (!this.thumbList) return;
        const hidden = new Set([...this.track.children].filter((s) => s.hidden).map((s) => s.dataset.mediaId));
        this.thumbList.querySelectorAll('[data-target]').forEach((t) => (t.hidden = hidden.has(t.dataset.target)));
        this.classList.toggle('product-gallery--few-thumbs', this.slides().length < 2);
        this.updateThumbArrows();
      }

      // Thumbnail carousel (thumbnail_slider): move by one visible thumbnail.
      scrollThumbs(dir) {
        const list = this.thumbList;
        const thumb = [...list.children].find((t) => !t.hidden);
        const step = thumb ? thumb.offsetWidth + parseFloat(getComputedStyle(list).columnGap || 0) : list.clientWidth;
        list.scrollBy({ left: dir * step, behavior: 'smooth' });
      }

      updateThumbArrows() {
        if (!this.thumbList || !(this.thumbPrev || this.thumbNext)) return;
        const list = this.thumbList;
        const max = list.scrollWidth - list.clientWidth;
        if (this.thumbPrev) this.thumbPrev.disabled = list.scrollLeft <= 1;
        if (this.thumbNext) this.thumbNext.disabled = list.scrollLeft >= max - 1;
      }

      buildDots() {
        const count = this.slides().length;
        this.classList.toggle('product-gallery--single', count < 2);
        if (this.dots) {
          this.dots.innerHTML = Array.from(
            { length: count },
            (_, i) => `<button type="button" class="product-gallery__dot" data-index="${i}" aria-label="${i + 1} / ${count}"></button>`
          ).join('');
        }
        const total = this.counter?.querySelector('[data-counter-total]');
        if (total) total.textContent = count;
      }

      // Called by the product controller (product-page.js) when the variant changes.
      onVariantChange(variant, prepend = true) {
        const filterIndex = Number(this.dataset.filterIndex) || 0;
        const hideVariants = this.dataset.hideVariants === 'true';
        const variantMedia = this.dataset.variantMedia || ',';
        const selectedMediaId = variant.featured_media ? String(variant.featured_media.id) : null;
        const filterValue = filterIndex ? String(variant.options[filterIndex - 1] || '').toLowerCase().trim() : '';
        const all = [...this.track.children];

        if (filterIndex || hideVariants) {
          all.forEach((slide) => {
            const mediaId = slide.dataset.mediaId.split('-').pop();
            let hidden = false;
            if (filterIndex && slide.dataset.group && slide.dataset.group !== filterValue) hidden = true;
            if (hideVariants && variantMedia.includes(`,${mediaId},`) && selectedMediaId && mediaId !== selectedMediaId) hidden = true;
            slide.hidden = hidden;
          });
          if (!all.some((s) => !s.hidden)) all.forEach((s) => (s.hidden = false));
          this.syncThumbs();
          this.buildDots();
        }

        if (selectedMediaId) {
          this.setActiveMedia(`${this.sectionId}-${selectedMediaId}`, prepend && this.dataset.prepend !== 'false');
        } else {
          this.goTo(0, 'instant');
        }
      }

      setActiveMedia(mediaId, prepend, userInitiated = false) {
        const slide = this.track.querySelector(`[data-media-id="${mediaId}"]`);
        if (!slide || slide.hidden) {
          if (!this.activeSlide()?.isConnected || this.activeSlide()?.hidden) this.goTo(0, 'instant');
          return;
        }
        if (prepend) {
          this.track.prepend(slide);
          const thumb = this.thumbList?.querySelector(`[data-target="${mediaId}"]`);
          if (thumb) thumb.parentElement.prepend(thumb);
          this.track.scrollTo({ left: 0, behavior: 'auto' });
        }
        this.setActive(slide, { scroll: true, behavior: prepend ? 'instant' : 'smooth' });
        if (!this.isSlider() && !prepend && !userInitiated) {
          slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        if (userInitiated && this.status) {
          const index = this.slides().indexOf(slide) + 1;
          this.status.textContent = (window.accessibilityStrings?.imageAvailable || '').replace('[index]', index);
        }
      }
    }
  );
}

if (!customElements.get('product-video')) {
  customElements.define(
    'product-video',
    class ProductVideo extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.ready = true;
        this.autoplay = this.hasAttribute('data-autoplay');
        this.video.playsInline = true;
        if (this.autoplay) this.video.muted = true;

        const toggle = this.querySelector('[data-video-toggle]');
        toggle?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.video.paused ? this.play(true) : this.pause(true);
        });
        if (!this.autoplay || toggle) {
          this.video.addEventListener('click', () => (this.video.paused ? this.play(true) : this.pause(true)));
        }

        const sound = this.querySelector('[data-video-sound]');
        sound?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.video.muted = !this.video.muted;
          this.syncSound(sound);
        });
        if (sound) this.syncSound(sound);

        const timeline = this.querySelector('[data-video-timeline]');
        if (timeline) {
          this.video.addEventListener('timeupdate', () => {
            const pct = this.video.duration ? (this.video.currentTime / this.video.duration) * 100 : 0;
            timeline.style.setProperty('--progress', `${pct}%`);
            timeline.setAttribute('aria-valuenow', Math.round(pct));
          });
          const seek = (clientX) => {
            const rect = timeline.getBoundingClientRect();
            const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
            if (this.video.duration) this.video.currentTime = ratio * this.video.duration;
          };
          timeline.addEventListener('click', (e) => {
            e.stopPropagation();
            seek(e.clientX);
          });
          timeline.addEventListener('keydown', (e) => {
            if (!this.video.duration) return;
            if (e.key === 'ArrowRight') this.video.currentTime = Math.min(this.video.duration, this.video.currentTime + 5);
            if (e.key === 'ArrowLeft') this.video.currentTime = Math.max(0, this.video.currentTime - 5);
          });
        }

        this.video.addEventListener('play', () => this.classList.add('is-playing'));
        this.video.addEventListener('pause', () => this.classList.remove('is-playing'));

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              if (this.autoplay && !this.userPaused && !reduce) this.play(false);
            } else if (!this.video.paused) {
              this.pause(false);
            }
          },
          { threshold: [0, 0.5] }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      syncSound(button) {
        button.setAttribute('aria-pressed', this.video.muted ? 'false' : 'true');
        this.classList.toggle('is-muted', this.video.muted);
      }

      play(user) {
        if (user) this.userPaused = false;
        const p = this.video.play();
        if (p && p.catch) p.catch(() => {});
      }

      pause(user) {
        if (user) this.userPaused = true;
        this.video.pause();
      }
    }
  );
}
